/**
 * SPEC-UnifiedQADetail — `useViewMode` hook.
 *
 * Layout primitive (NOT a permission primitive). The hook reads + writes
 * `localStorage.qaViewMode` and listens for cross-tab storage events.
 *
 * Permission gates always run against `useRole()` — `viewMode` is a UI
 * preference that toggle-capable callers (senior / admin) can flip without
 * touching their identity. The server-side `agent-payload-filter` fires
 * only on `actualRole === 'agent'`, never on viewMode.
 *
 * The pure logic (default-by-role, agent pin, validation) lives in
 * `lib/view-mode-helpers.ts` and is unit-tested without jsdom. This wrapper
 * adds the React state + storage plumbing and is exercised by the v4 smoke
 * test in Phase 2.
 */

import { useCallback, useEffect, useState } from "react";
import { useRole } from "./use-role";
import {
  VIEW_MODE_STORAGE_KEY,
  canToggleViewMode,
  coerceStoredViewMode,
  defaultViewMode,
  type ViewMode,
} from "../lib/view-mode-helpers";

export type { ViewMode };

interface UseViewModeResult {
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  /** True when the toggle should render (senior / admin only). */
  canToggle: boolean;
}

function readStored(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: ViewMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, value);
  } catch {
    /* ignore — Safari private mode and similar */
  }
}

export function useViewMode(): UseViewModeResult {
  const { role } = useRole();
  const canToggle = canToggleViewMode(role);

  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    coerceStoredViewMode(readStored(), role),
  );

  // Re-resolve when role lands or changes (e.g. auth payload arrives post-mount).
  useEffect(() => {
    setViewModeState(coerceStoredViewMode(readStored(), role));
  }, [role]);

  // Cross-tab sync — storage event fires on OTHER tabs only. Re-coerce so
  // agent pins still hold even if another tab wrote a bogus value.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key !== VIEW_MODE_STORAGE_KEY) return;
      setViewModeState(coerceStoredViewMode(e.newValue, role));
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [role]);

  const setViewMode = useCallback(
    (m: ViewMode) => {
      if (!canToggle) return; // agents / bots / null are pinned to default
      writeStored(m);
      setViewModeState(coerceStoredViewMode(m, role));
    },
    [canToggle, role],
  );

  // Final defensive coerce in case role changed underfoot.
  const resolved =
    role === "agent" ? "agent" : canToggle ? viewMode : defaultViewMode(role);

  return { viewMode: resolved, setViewMode, canToggle };
}
